import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { Form, Nav, Tab } from "react-bootstrap";
import MyIcon from "../Common/MyIcon";
import SolutionIcon from "../../assets/icons/Icon_Library/solutions icons/app_colouring/sol_resource_managment_fulfilment-01.svg2";
import SolutionFlowOverview from "../Solutions/SolutionFlowOverview";
import AiIcon from "../../assets/icons/Icon_Library/application icons/library/boomAI_icon.svg2";
import ProfileIcon from "../../assets/icons/Icon_Library/application icons/general/profile_fields.svg2";
import SolutionStep from "./SolutionStep";
import SolutionStepInputGroup from "./SolutionStepInputGroup";
import InputArgumentField from "../Builder/Components/sidebar/ProjectStartForm/InputArgumentField";
import SolutionImportInput from "../Solutions/SolutionImportInput";
import { parseSolutionData } from "../Solutions/services/jsonParsingService";
import { useGetContactSetsQuery } from "../store/slices/contactsSlice";
import { useGetReportingTopicsQuery } from "../store/slices/reportingSlice";
import {
  SolutionType,
  useUpdateApiConnectionMutation,
} from "../store/slices/settingsSlice";
import {
  initializeWeatherMonitor,
  initializePingMonitor,
  initializeInboundCampaign,
} from "../Solutions/services/deploymentService";
import MyCollapse from "../Common/MyCollapse";
import MyModal from "../Common/MyModal";

interface SolutionSetupProps {
  solution?: SolutionType;
  setSolution?: React.Dispatch<React.SetStateAction<SolutionType>>;
  importedJSON?: any;
  setImportedJSON?: React.Dispatch<React.SetStateAction<any>>;
  manualFields?: string[];
  setManualFields?: React.Dispatch<React.SetStateAction<string[]>>;
  initialVariableDefaults?: Record<string, any>;
  onChange?: (formData: Record<string, any>) => void;
  onMonitorsChange?: (monitors: Record<string, any>) => void;
  onInboundAPIsChange?: (inboundAPIs: Record<string, any>) => void;
  onInboundCampaignsChange?: (inboundCampaigns: Record<string, any>) => void;
}

type SidebarDragArg = any;

interface SidebarDragState {
  arg: SidebarDragArg;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SidebarOverInputState {
  groupId: string;
  index: number;
  mode?: "insert" | "merge";
  targetIndex?: number;
  mergeCandidateIndex?: number;
}

interface NavDragGhostState {
  label: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

interface FlowNodeSelection {
  nodeKind: "solution" | "step" | "group" | "input";
  action?: "edit";
  stepIndex?: number;
  groupId?: string;
  inputIndex?: number;
  inputFocusKey?: string;
}

interface PendingFlowSelection extends FlowNodeSelection {
  attempts: number;
}

interface DiagramInputModalState {
  stepIndex: number;
  groupId: string;
  inputIndex: number;
}

const SolutionSetup: React.FC<SolutionSetupProps> = ({
  solution,
  setSolution = undefined,
  importedJSON: importedJSONProp = undefined,
  setImportedJSON: setImportedJSONProp = undefined,
  manualFields: manualFieldsProp = undefined,
  setManualFields: setManualFieldsProp = undefined,
  initialVariableDefaults = undefined,
  onChange = undefined,
  onMonitorsChange = undefined,
  onInboundAPIsChange = undefined,
  onInboundCampaignsChange = undefined,
}) => {
  const [localImportedJSON, setLocalImportedJSON] = React.useState<any>({
    Bag: { Internal: {}, External: {} },
    Item: {},
  });
  const importedJSON = importedJSONProp ?? localImportedJSON;
  const setImportedJSON = setImportedJSONProp ?? setLocalImportedJSON;

  const [localManualFields, setLocalManualFields] = useState<string[]>([]);
  const manualFields = manualFieldsProp ?? localManualFields;
  const setManualFields = setManualFieldsProp ?? setLocalManualFields;

  useEffect(() => {
    if (!solution) return;
    const { importedJSON: parsed } = parseSolutionData(solution);
    setImportedJSON(parsed);
  }, [solution]);

  const [apiProfiles, setApiProfiles] = useState<any>({});
  const [monitors, setMonitors] = useState<any>({});
  const [inboundAPIs, setInboundAPIs] = useState<any>({});
  const [inboundCampaigns, setInboundCampaigns] = useState<any>({});

  useEffect(() => {
    if (!solution?.Components) return;
    solution.Components.forEach((comp: any) => {
      if (comp.Role === "weather-monitor") {
        setMonitors((prev: any) => ({
          ...prev,
          ...initializeWeatherMonitor(comp.ComponentUuid),
        }));
      }
      if (comp.Role === "ping-monitor") {
        setMonitors((prev: any) => ({
          ...prev,
          ...initializePingMonitor(comp.ComponentUuid),
        }));
      }
      if (comp.Role === "inbound-message") {
        setInboundCampaigns((prev: any) => ({
          ...prev,
          ...initializeInboundCampaign(comp.ComponentUuid),
        }));
      }
    });
  }, [solution?.Components]);

  useEffect(() => {
    onMonitorsChange?.(monitors);
  }, [monitors, onMonitorsChange]);
  useEffect(() => {
    onInboundAPIsChange?.(inboundAPIs);
  }, [inboundAPIs, onInboundAPIsChange]);
  useEffect(() => {
    onInboundCampaignsChange?.(inboundCampaigns);
  }, [inboundCampaigns, onInboundCampaignsChange]);
  const { data: contactSetsData } = useGetContactSetsQuery({
    page: 0,
    perPage: 100,
  });
  const { data: reportingTopicsData } = useGetReportingTopicsQuery({
    pagination: { page: 0, perPage: 100 },
  });
  const [updateApiConnection] = useUpdateApiConnectionMutation();

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [navDragFromIndex, setNavDragFromIndex] = useState<number | null>(null);
  const [navDropRegion, setNavDropRegion] = useState<{
    index: number;
    side: "left" | "right";
  } | null>(null);
  const [navDragGhost, setNavDragGhost] = useState<NavDragGhostState | null>(
    null
  );
  const isAdminMode = !!setSolution;
  const isUserMode = !isAdminMode;
  const disableInputEditing = isAdminMode;
  const disableInputDragging = !isAdminMode;
  const [formData, setFormData] = useState<Record<string, any>>(
    initialVariableDefaults || {}
  );
  console.log({ initialVariableDefaults, formData });
  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  // ── sidebar ghost drag ───────────────────────────────────────────────────
  const sidebarGhostEl = useRef<HTMLDivElement | null>(null);
  const draggingSidebarArg = useRef<SidebarDragArg | null>(null);
  const [sidebarDragState, setSidebarDragState] =
    useState<SidebarDragState | null>(null);
  const [sidebarOverInput, setSidebarOverInput] =
    useState<SidebarOverInputState | null>(null); // { groupId, index } | null

  // Track focused step and input group for diagram highlighting
  const [focusedStepIndex, setFocusedStepIndex] = useState<number | null>(null);
  const [focusedInputGroupId, setFocusedInputGroupId] = useState<string | null>(
    null
  );
  const [focusedInputNodeKey, setFocusedInputNodeKey] = useState<string | null>(
    null
  );
  const setupFormContainerRef = useRef<HTMLDivElement | null>(null);
  const [pendingFlowSelection, setPendingFlowSelection] =
    useState<PendingFlowSelection | null>(null);
  const [diagramInputModal, setDiagramInputModal] =
    useState<DiagramInputModalState | null>(null);

  const getInputFocusKey = useCallback(
    (stepIndex: number, inputGroupId: string, input: any) => {
      if (!inputGroupId) return null;
      const identity =
        input?.UUID ||
        (input?.ComponentUuid && input?.Field
          ? `${input.ComponentUuid}:${input.Field}`
          : null) ||
        (input?.ComponentUuid && input?.Name
          ? `${input.ComponentUuid}:${input.Name}`
          : null) ||
        (input?.ComponentUuid && input?.Label
          ? `${input.ComponentUuid}:${input.Label}`
          : null) ||
        (input?.__focusIndex !== undefined ? `${input.__focusIndex}` : null) ||
        "unknown";
      return `step:${stepIndex}|group:${inputGroupId}|input:${identity}`;
    },
    []
  );

  const findInputCardElement = useCallback(
    (container: HTMLElement, groupId: string, inputIndex: number) => {
      const activePane =
        container.querySelector<HTMLElement>(".tab-pane.active") || container;
      const cards = Array.from(
        activePane.querySelectorAll<HTMLElement>(
          "[data-group-id][data-input-item]"
        )
      );

      return (
        cards.find(
          (card) =>
            card.dataset.groupId === groupId &&
            Number(card.dataset.inputItem) === inputIndex
        ) || null
      );
    },
    []
  );

  const findGroupZoneElement = useCallback(
    (container: HTMLElement, groupId: string) => {
      const activePane =
        container.querySelector<HTMLElement>(".tab-pane.active") || container;
      const zones = Array.from(
        activePane.querySelectorAll<HTMLElement>("[data-input-zone]")
      );

      return zones.find((zone) => zone.dataset.inputZone === groupId) || null;
    },
    []
  );

  const focusFirstEditableField = useCallback((cardEl: HTMLElement) => {
    const firstField = cardEl.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"], [aria-autocomplete], [contenteditable="true"]'
    );
    firstField?.focus();
  }, []);

  const focusStep = useCallback((stepIndex: number) => {
    setActiveStepIndex(stepIndex);
    setFocusedStepIndex(stepIndex);
    setFocusedInputGroupId(null);
    setFocusedInputNodeKey(null);
  }, []);

  const handleFlowNodeClick = useCallback(
    (selection: FlowNodeSelection) => {
      if (selection?.stepIndex === undefined || selection?.stepIndex === null) {
        return;
      }

      const stepIndex = selection.stepIndex;
      const step = solution?.ComponentConfig?.steps?.[stepIndex];
      if (!step) return;

      focusStep(stepIndex);

      if (selection.nodeKind === "step") {
        setPendingFlowSelection(null);
        return;
      }

      if (selection.nodeKind === "group") {
        if (!selection.groupId) return;
        setFocusedInputGroupId(`step:${stepIndex}|group:${selection.groupId}`);
        setFocusedInputNodeKey(null);
        setPendingFlowSelection({ ...selection, attempts: 0 });
        return;
      }

      if (selection.nodeKind === "input") {
        if (!selection.groupId || typeof selection.inputIndex !== "number") {
          return;
        }

        const group = (step.inputGroups || []).find(
          (item: any) => item.id === selection.groupId
        );
        const input = group?.inputs?.[selection.inputIndex];
        const resolvedInputNodeKey =
          selection.inputFocusKey ||
          (input
            ? getInputFocusKey(stepIndex, selection.groupId, {
                ...input,
                __focusIndex: selection.inputIndex,
              })
            : null);

        setFocusedInputGroupId(`step:${stepIndex}|group:${selection.groupId}`);
        setFocusedInputNodeKey(resolvedInputNodeKey);
        if (selection.action === "edit") {
          setDiagramInputModal({
            stepIndex,
            groupId: selection.groupId,
            inputIndex: selection.inputIndex,
          });
          setPendingFlowSelection(null);
          return;
        }

        setPendingFlowSelection({ ...selection, attempts: 0 });
      }
    },
    [focusStep, getInputFocusKey, solution?.ComponentConfig?.steps]
  );

  useEffect(() => {
    if (!pendingFlowSelection) return;
    if (pendingFlowSelection.stepIndex !== activeStepIndex) return;

    const container = setupFormContainerRef.current;
    if (!container) return;

    let targetEl: HTMLElement | null = null;

    if (
      pendingFlowSelection.nodeKind === "input" &&
      pendingFlowSelection.groupId &&
      typeof pendingFlowSelection.inputIndex === "number"
    ) {
      targetEl = findInputCardElement(
        container,
        pendingFlowSelection.groupId,
        pendingFlowSelection.inputIndex
      );
    } else if (
      pendingFlowSelection.nodeKind === "group" &&
      pendingFlowSelection.groupId
    ) {
      targetEl = findGroupZoneElement(container, pendingFlowSelection.groupId);
    }

    if (targetEl) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      if (pendingFlowSelection.nodeKind === "input") {
        focusFirstEditableField(targetEl);
      }
      setPendingFlowSelection(null);
      return;
    }

    if (pendingFlowSelection.attempts >= 6) {
      setPendingFlowSelection(null);
      return;
    }

    const retryTimer = window.setTimeout(() => {
      setPendingFlowSelection((prev) =>
        prev
          ? {
              ...prev,
              attempts: prev.attempts + 1,
            }
          : prev
      );
    }, 70);

    return () => {
      window.clearTimeout(retryTimer);
    };
  }, [
    activeStepIndex,
    findGroupZoneElement,
    findInputCardElement,
    focusFirstEditableField,
    pendingFlowSelection,
  ]);

  const remapIndexAfterMove = useCallback(
    (currentIndex: number, fromIndex: number, insertIndex: number) => {
      const finalIndex =
        fromIndex < insertIndex ? insertIndex - 1 : insertIndex;
      if (currentIndex === fromIndex) return finalIndex;

      if (fromIndex < finalIndex) {
        if (currentIndex > fromIndex && currentIndex <= finalIndex)
          return currentIndex - 1;
        return currentIndex;
      }

      if (fromIndex > finalIndex) {
        if (currentIndex >= finalIndex && currentIndex < fromIndex)
          return currentIndex + 1;
      }

      return currentIndex;
    },
    []
  );

  const handleNavStepDragStart = useCallback(
    (
      e: React.DragEvent<HTMLElement>,
      index: number,
      label: string,
      width: number,
      height: number
    ) => {
      if (!setSolution) return;

      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(index));

      const transparent = document.createElement("img");
      transparent.src =
        "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3C/svg%3E";
      e.dataTransfer.setDragImage(transparent, 0, 0);

      setNavDragFromIndex(index);
      setNavDropRegion({ index, side: "right" });
      setNavDragGhost({
        label,
        top: e.clientY - height / 2,
        left: e.clientX - width / 2,
        width,
        height,
      });
    },
    [setSolution]
  );

  const handleNavStepDragOver = useCallback(
    (e: React.DragEvent<HTMLElement>, index: number) => {
      if (!setSolution) return;
      e.preventDefault();

      const rect = e.currentTarget.getBoundingClientRect();
      const side = e.clientX <= rect.left + rect.width / 2 ? "left" : "right";
      setNavDropRegion((prev) =>
        prev && prev.index === index && prev.side === side
          ? prev
          : { index, side }
      );

      setNavDragGhost((prev) =>
        prev
          ? {
              ...prev,
              top: e.clientY - prev.height / 2,
              left: e.clientX - prev.width / 2,
            }
          : prev
      );
    },
    [setSolution]
  );

  const resetNavDragState = useCallback(() => {
    setNavDragFromIndex(null);
    setNavDropRegion(null);
    setNavDragGhost(null);
  }, []);

  const handleNavStepDrop = useCallback(
    (toIndex: number, side: "left" | "right") => {
      if (!setSolution || navDragFromIndex === null) {
        resetNavDragState();
        return;
      }

      const fromIndex = navDragFromIndex;
      const insertIndex = side === "left" ? toIndex : toIndex + 1;
      const finalIndex =
        fromIndex < insertIndex ? insertIndex - 1 : insertIndex;
      if (fromIndex === finalIndex) {
        resetNavDragState();
        return;
      }

      setSolution((prevSolution: any) => {
        const prevSteps = prevSolution?.ComponentConfig?.steps || [];
        if (
          !prevSteps.length ||
          fromIndex < 0 ||
          insertIndex < 0 ||
          fromIndex >= prevSteps.length ||
          insertIndex > prevSteps.length
        ) {
          return prevSolution;
        }

        const reorderedSteps = [...prevSteps];
        const [movedStep] = reorderedSteps.splice(fromIndex, 1);
        const adjustedInsertIndex =
          fromIndex < insertIndex ? insertIndex - 1 : insertIndex;
        reorderedSteps.splice(adjustedInsertIndex, 0, movedStep);

        return {
          ...prevSolution,
          ComponentConfig: {
            ...prevSolution.ComponentConfig,
            steps: reorderedSteps,
          },
        };
      });

      setActiveStepIndex((prev) =>
        remapIndexAfterMove(prev, fromIndex, insertIndex)
      );
      setFocusedStepIndex((prev) =>
        prev === null ? null : remapIndexAfterMove(prev, fromIndex, insertIndex)
      );
      resetNavDragState();
    },
    [navDragFromIndex, remapIndexAfterMove, resetNavDragState, setSolution]
  );

  const getSidebarMergeRef = useCallback(
    (arg: any, fallbackComponentUuid?: string) => {
      if (!arg || typeof arg !== "object") return null;

      if (arg.__kind === "external") {
        if (!arg.fieldType || !arg.fieldKey) return null;
        return {
          __kind: "external",
          fieldType: arg.fieldType,
          fieldKey: arg.fieldKey,
          ComponentUuid: `external:${arg.fieldType}`,
          Field: arg.fieldKey,
        };
      }

      if (arg.__kind) return null;

      const componentUuid = fallbackComponentUuid || arg.ComponentUuid;
      if (!componentUuid || !arg.Field) return null;
      return { ComponentUuid: componentUuid, Field: arg.Field };
    },
    []
  );

  const canSidebarMergeWithInput = useCallback(
    (draggedArg: any, targetInput: any, draggedComponentUuid?: string) => {
      if (!draggedArg || !targetInput) return false;

      if (
        draggedArg?.__kind === "external" ||
        targetInput?.__kind === "external"
      ) {
        return (
          draggedArg?.__kind === "external" &&
          targetInput?.__kind === "external" &&
          !!draggedArg?.fieldType &&
          draggedArg.fieldType === targetInput.fieldType &&
          !!draggedArg?.fieldKey &&
          !!targetInput?.fieldKey &&
          draggedArg.fieldKey !== targetInput.fieldKey
        );
      }

      if (draggedArg?.__kind || targetInput?.__kind) return false;
      if (!draggedArg?.Format || !targetInput?.Format) return false;
      if (draggedArg.Format !== targetInput.Format) return false;

      const sourceRef = getSidebarMergeRef(draggedArg, draggedComponentUuid);
      const targetRef = getSidebarMergeRef(
        targetInput,
        targetInput?.ComponentUuid
      );
      if (!sourceRef || !targetRef) return false;

      return !(
        sourceRef.ComponentUuid === targetRef.ComponentUuid &&
        sourceRef.Field === targetRef.Field
      );
    },
    [getSidebarMergeRef]
  );

  const onSidebarArgPointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLElement>,
      arg: SidebarDragArg,
      ComponentUuid?: string
    ) => {
      console.log("sidebar", e, arg, ComponentUuid);
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      draggingSidebarArg.current = ComponentUuid
        ? { ...arg, ComponentUuid }
        : arg;

      setSidebarDragState({
        arg,
        top: e.clientY - rect.height / 2,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });

      const resolveTarget = (
        clientX: number,
        clientY: number
      ): HTMLElement | null => {
        if (sidebarGhostEl.current)
          sidebarGhostEl.current.style.pointerEvents = "none";
        const target = document.elementFromPoint(clientX, clientY);
        if (sidebarGhostEl.current)
          sidebarGhostEl.current.style.pointerEvents = "";
        return target as HTMLElement | null;
      };

      const onMove = (moveEvt: PointerEvent) => {
        if (sidebarGhostEl.current) {
          sidebarGhostEl.current.style.top = `${
            moveEvt.clientY - rect.height / 2
          }px`;
          sidebarGhostEl.current.style.left = `${
            moveEvt.clientX - rect.width / 2
          }px`;
        }

        const target = resolveTarget(moveEvt.clientX, moveEvt.clientY);
        if (!target) {
          setSidebarOverInput(null);
          return;
        }

        const inputEl = target.closest(
          "[data-input-item]"
        ) as HTMLElement | null;
        if (inputEl) {
          const gId = inputEl.dataset.groupId ?? "";
          if (gId === "") return;
          const idx = Number(inputEl.dataset.inputItem ?? 0);
          const itemRect = inputEl.getBoundingClientRect();
          const relativeY = moveEvt.clientY - itemRect.top;
          const topThirdEnd = itemRect.height * 0.33;
          const bottomThirdStart = itemRect.height * 0.67;
          const insertIdx =
            relativeY < topThirdEnd
              ? idx
              : relativeY > bottomThirdStart
              ? idx + 1
              : idx;

          const draggedArg = draggingSidebarArg.current;
          const targetInput = solution?.ComponentConfig?.steps
            ?.flatMap((step: any) => step.inputGroups || [])
            ?.find((group: any) => group.id === gId)?.inputs?.[idx];

          const canMerge = canSidebarMergeWithInput(
            draggedArg,
            targetInput,
            ComponentUuid
          );

          const nextOver =
            canMerge &&
            relativeY >= topThirdEnd &&
            relativeY <= bottomThirdStart
              ? {
                  groupId: gId,
                  index: insertIdx,
                  mode: "merge" as const,
                  targetIndex: idx,
                  mergeCandidateIndex: idx,
                }
              : {
                  groupId: gId,
                  index: insertIdx,
                  mode: "insert" as const,
                  targetIndex: undefined,
                  mergeCandidateIndex: undefined,
                };

          setSidebarOverInput((prev) =>
            prev?.groupId === gId &&
            prev?.index === insertIdx &&
            prev?.mode === nextOver.mode &&
            prev?.targetIndex === nextOver.targetIndex &&
            prev?.mergeCandidateIndex === nextOver.mergeCandidateIndex
              ? prev
              : nextOver
          );
          return;
        }
        const zoneEl = target.closest(
          "[data-input-zone]"
        ) as HTMLElement | null;
        if (zoneEl) {
          const gId = zoneEl.dataset.inputZone ?? "";
          if (gId === "") return;
          const count = Number(zoneEl.dataset.inputCount ?? 0);
          setSidebarOverInput((prev) =>
            prev?.groupId === gId && prev?.index === count
              ? prev
              : {
                  groupId: gId,
                  index: count,
                  mode: "insert",
                  targetIndex: undefined,
                  mergeCandidateIndex: undefined,
                }
          );
          return;
        }
        setSidebarOverInput(null);
      };

      const onUp = (upEvt: PointerEvent) => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);

        const draggedArg = draggingSidebarArg.current;
        draggingSidebarArg.current = null;
        setSidebarDragState(null);
        setSidebarOverInput(null);

        if (!draggedArg || typeof draggedArg !== "object") return;

        const target = resolveTarget(upEvt.clientX, upEvt.clientY);
        if (!target) return;

        let targetGroupId: string | null = null;
        let insertIndex: number | null = null;
        let mergeTargetIndex: number | null = null;

        const inputEl = target.closest(
          "[data-input-item]"
        ) as HTMLElement | null;
        if (inputEl) {
          const gId = inputEl.dataset.groupId ?? "";
          targetGroupId = gId === "" ? null : gId;
          const idx = Number(inputEl.dataset.inputItem ?? 0);
          const itemRect = inputEl.getBoundingClientRect();
          const relativeY = upEvt.clientY - itemRect.top;
          const topThirdEnd = itemRect.height * 0.33;
          const bottomThirdStart = itemRect.height * 0.67;
          insertIndex =
            relativeY < topThirdEnd
              ? idx
              : relativeY > bottomThirdStart
              ? idx + 1
              : idx;

          const allGroups = solution?.ComponentConfig?.steps
            ?.flatMap((step: any) => step.inputGroups || [])
            ?.filter(Boolean);
          const targetInput = allGroups?.find(
            (group: any) => group.id === targetGroupId
          )?.inputs?.[idx];
          const canMerge = canSidebarMergeWithInput(
            draggedArg,
            targetInput,
            ComponentUuid
          );
          if (
            canMerge &&
            relativeY >= topThirdEnd &&
            relativeY <= bottomThirdStart
          ) {
            mergeTargetIndex = idx;
          }
        } else {
          const zoneEl = target.closest(
            "[data-input-zone]"
          ) as HTMLElement | null;
          if (zoneEl) {
            const gId = zoneEl.dataset.inputZone ?? "";
            targetGroupId = gId === "" ? null : gId;
            insertIndex = Number(zoneEl.dataset.inputCount ?? 0);
          }
        }

        if (!targetGroupId) return;

        setSolution?.((prev: SolutionType) => {
          if (!prev?.ComponentConfig?.steps) return prev;
          return {
            ...prev,
            ComponentConfig: {
              ...prev.ComponentConfig,
              steps: prev.ComponentConfig.steps.map((step: any) => ({
                ...step,
                inputGroups: step.inputGroups.map((group: any) => {
                  if (group.id !== targetGroupId) return group;
                  const newInputs = [...group.inputs];

                  if (
                    mergeTargetIndex !== null &&
                    mergeTargetIndex >= 0 &&
                    mergeTargetIndex < newInputs.length
                  ) {
                    const targetInput = newInputs[mergeTargetIndex];
                    const sourceRef = getSidebarMergeRef(
                      draggedArg,
                      ComponentUuid
                    );
                    if (!sourceRef) return { ...group, inputs: newInputs };
                    const mergedRefs = [
                      ...(targetInput?.MergedInputRefs || []),
                    ].filter(
                      (ref: any) =>
                        !(
                          ref?.ComponentUuid === sourceRef.ComponentUuid &&
                          ref?.Field === sourceRef.Field
                        )
                    );
                    mergedRefs.push(sourceRef);

                    newInputs[mergeTargetIndex] = {
                      ...targetInput,
                      MergedInputRefs: mergedRefs,
                    };

                    return { ...group, inputs: newInputs };
                  }

                  if (insertIndex !== null && insertIndex < newInputs.length) {
                    newInputs.splice(
                      insertIndex,
                      0,
                      ComponentUuid
                        ? { ...draggedArg, ComponentUuid }
                        : { ...draggedArg }
                    );
                  } else {
                    newInputs.push(
                      ComponentUuid
                        ? { ...draggedArg, ComponentUuid }
                        : { ...draggedArg }
                    );
                  }
                  return { ...group, inputs: newInputs };
                }),
              })),
            },
          };
        });
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    [
      canSidebarMergeWithInput,
      getSidebarMergeRef,
      setSolution,
      solution?.ComponentConfig?.steps,
    ]
  );

  const getExternalFieldLabel = useCallback(
    (fieldType: string, key: string, value: any) => {
      try {
        const parsed = JSON.parse(key);
        if (parsed?.label) return parsed.label;
        if (parsed?.Label) return parsed.Label;
        if (parsed?.field?.label && parsed?.option?.label) {
          return `${parsed.field.label} / ${parsed.option.label}`;
        }
        if (parsed?.option?.label) return parsed.option.label;
        if (parsed?.field?.label) return parsed.field.label;
      } catch (error) {
        // Some keys are plain UUIDs and not JSON strings.
      }

      if (value && typeof value === "object") {
        if (value.label) return value.label;
        if (value.Label) return value.Label;
      }

      return key;
    },
    []
  );

  const toExternalDragArg = useCallback(
    (fieldType: string, fieldKey: string, fieldValue: any) => ({
      __kind: "external",
      fieldType,
      fieldKey,
      fieldValue,
      UUID: `external:${fieldType}:${fieldKey}`,
      Label: getExternalFieldLabel(fieldType, fieldKey, fieldValue),
      Description: `External ${fieldType}`,
    }),
    [getExternalFieldLabel]
  );

  const toComponentConfigDragArgs = useCallback((component: any) => {
    if (
      component.Role === "weather-monitor" ||
      component.Role === "ping-monitor"
    ) {
      return [
        {
          __kind: "component-config",
          configType: component.Role,
          componentUuid: component.ComponentUuid,
          UUID: `component-config:${component.ComponentUuid}:monitor`,
          Label: "Monitor Config",
          Description: component.Item?.Label,
        },
      ];
    }

    if (component.Role === "inbound-api") {
      return [
        {
          __kind: "component-config",
          configType: "inbound-api",
          componentUuid: component.ComponentUuid,
          UUID: `component-config:${component.ComponentUuid}:inbound-api`,
          Label: "API Connection",
          Description: component.Item?.Label,
        },
      ];
    }

    if (component.Role === "inbound-message") {
      return [
        {
          __kind: "component-config",
          configType: "inbound-message",
          componentUuid: component.ComponentUuid,
          UUID: `component-config:${component.ComponentUuid}:inbound-message`,
          Label: "Inbound Channel",
          Description: component.Item?.Label,
        },
      ];
    }

    return [];
  }, []);

  const handleAddAll = useCallback(() => {
    if (!setSolution) return;

    setSolution((prev: SolutionType) => {
      const buildStep = (
        name: string,
        description: string,
        inputGroups: any[]
      ) => ({
        name,
        description,
        inputGroups,
      });

      const buildInputGroup = (
        id: string,
        name: string,
        description: string,
        inputs: any[]
      ) => ({
        id,
        name,
        description,
        inputs,
      });

      const sortByOrder = (items: any[]) =>
        [...items].sort((a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0));

      const existingSteps = (prev?.ComponentConfig?.steps || []).map(
        (step: any) => ({
          ...step,
          inputGroups: (step.inputGroups || []).map((group: any) => ({
            ...group,
            inputs: [...(group.inputs || [])],
          })),
        })
      );

      const usedUuids = new Set<string>(
        existingSteps.flatMap((step: any) =>
          (step.inputGroups || []).flatMap((group: any) =>
            (group.inputs || []).map((input: any) => input?.UUID)
          )
        )
      );

      const registerArgs = (args: any[]) =>
        args
          .map((arg, index) => {
            const normalizedUuid =
              arg?.UUID ||
              `component-input:${arg?.ComponentUuid || "arg"}:${
                arg?.Field || arg?.Label || index
              }`;
            return {
              ...arg,
              UUID: normalizedUuid,
              Label:
                arg?.Label || arg?.Field || arg?.Name || `Input ${index + 1}`,
              Description: arg?.Description || "",
            };
          })
          .filter((arg) => {
            if (usedUuids.has(arg.UUID)) return false;
            usedUuids.add(arg.UUID);
            return true;
          });

      const groupInputsByVariableGroups = (
        component: any,
        args: any[],
        groupPrefix: string,
        emptyGroupLabel: string
      ) => {
        const variableGroups = [...(component.Item?.VariableGroups || [])].sort(
          (a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0)
        );

        const grouped = args.reduce((acc: Record<string, any[]>, arg: any) => {
          const groupUuid = arg?.GroupUuid || "";
          if (!acc[groupUuid]) acc[groupUuid] = [];
          acc[groupUuid].push(arg);
          return acc;
        }, {});

        const groups = variableGroups.map((group: any, index: number) => {
          const groupInputs = sortByOrder(grouped[group.GroupUuid] || []);
          return buildInputGroup(
            `${groupPrefix}:${group.GroupUuid || index}`,
            group.Label || emptyGroupLabel,
            group.Description ||
              component.Item?.Description ||
              component.Description ||
              "",
            groupInputs
          );
        });

        const defaultGroupInputs = sortByOrder(grouped[""] || []);
        if (variableGroups.length === 0 && args.length > 0) {
          groups.push(
            buildInputGroup(
              `${groupPrefix}:default`,
              "Default Group",
              component.Item?.Description || component.Description || "",
              defaultGroupInputs.length > 0
                ? defaultGroupInputs
                : sortByOrder(args)
            )
          );
        }

        return groups;
      };

      const externalArgs = registerArgs(
        Object.entries(importedJSON?.Bag?.External || {}).flatMap(
          ([fieldType, fields]) =>
            Object.entries(fields || {}).map(([fieldKey, fieldValue]) =>
              toExternalDragArg(fieldType, fieldKey, fieldValue)
            )
        )
      );

      const externalStep = buildStep(
        "External Inputs",
        "Imported external fields.",
        externalArgs.length
          ? [
              buildInputGroup(
                "external-inputs",
                "External Fields",
                "External fields from imported JSON.",
                sortByOrder(externalArgs)
              ),
            ]
          : []
      );

      const componentConfigGroups = (solution?.Components || []).flatMap(
        (component: any, componentIndex: number) => {
          const configArgs = registerArgs(
            toComponentConfigDragArgs(component).map(
              (arg: any, index: number) => ({
                ...arg,
                ComponentUuid: component.ComponentUuid,
                UUID: `component-config:${component.ComponentUuid}:${
                  component.Role
                }:${arg?.configType || index}`,
              })
            )
          );

          if (configArgs.length === 0) return [];

          return [
            buildInputGroup(
              `component-config:${component.ComponentUuid}`,
              component.Item?.Label ||
                component.Role ||
                `Component ${componentIndex + 1}`,
              component.Item?.Description ||
                component.Description ||
                "Configure component options.",
              sortByOrder(configArgs)
            ),
          ];
        }
      );

      const componentSteps = (solution?.Components || []).flatMap(
        (component: any, componentIndex: number) => {
          const parameterArgs = registerArgs(
            (component.Item?.InputArguments || [])
              .filter(
                (arg: any) => arg.IsParameter && !arg.SolutionDoNotConfigure
              )
              .map((arg: any, index: number) => ({
                ...arg,
                ComponentUuid: component.ComponentUuid,
                UUID: `component-input:${component.ComponentUuid}:${
                  component.Role
                }:${arg?.Field || arg?.Label || arg?.UUID || index}`,
              }))
          );

          if (
            parameterArgs.length === 0 &&
            (component.Item?.VariableGroups || []).length === 0
          ) {
            return [];
          }

          return [
            buildStep(
              component.Item?.Label ||
                component.Role ||
                `Component ${componentIndex + 1}`,
              component.Item?.Description ||
                component.Description ||
                "Configure component inputs.",
              groupInputsByVariableGroups(
                component,
                parameterArgs,
                `component-step:${component.ComponentUuid}:${component.Role}`,
                "Input Group"
              )
            ),
          ];
        }
      );

      const ensureStep = (
        matcher: (step: any) => boolean,
        factory: () => any
      ) => {
        let index = existingSteps.findIndex(matcher);
        if (index === -1) {
          const created = factory();
          existingSteps.push(created);
          index = existingSteps.length - 1;
        }

        if (!existingSteps[index].inputGroups) {
          existingSteps[index].inputGroups = [];
        }

        return existingSteps[index];
      };

      const upsertGroupInputs = (step: any, groupTemplate: any) => {
        if (!step.inputGroups) step.inputGroups = [];

        const groupIndex = step.inputGroups.findIndex(
          (group: any) => group.id === groupTemplate.id
        );

        if (groupIndex === -1) {
          if ((groupTemplate.inputs || []).length === 0) return;
          step.inputGroups.push({
            ...groupTemplate,
            inputs: [...(groupTemplate.inputs || [])],
          });
          return;
        }

        const existingGroup = step.inputGroups[groupIndex];
        const existingInputUuids = new Set(
          (existingGroup.inputs || []).map((input: any) => input.UUID)
        );
        const additions = (groupTemplate.inputs || []).filter(
          (input: any) => !existingInputUuids.has(input.UUID)
        );

        if (additions.length) {
          existingGroup.inputs = [
            ...(existingGroup.inputs || []),
            ...additions,
          ];
        }
      };

      const externalStepTarget = ensureStep(
        (step: any) =>
          step?.name === "External Inputs" ||
          step?.inputGroups?.some(
            (group: any) => group.id === "external-inputs"
          ),
        () => buildStep("External Inputs", "Imported external fields.", [])
      );

      (externalStep.inputGroups || []).forEach((group: any) => {
        upsertGroupInputs(externalStepTarget, group);
      });

      const componentConfigStepTarget = ensureStep(
        (step: any) =>
          step?.name === "Component Configuration" ||
          step?.inputGroups?.some((group: any) =>
            String(group?.id || "").startsWith("component-config:")
          ),
        () =>
          buildStep(
            "Component Configuration",
            "Configure monitor, API, and inbound message options.",
            []
          )
      );

      componentConfigGroups.forEach((group: any) => {
        upsertGroupInputs(componentConfigStepTarget, group);
      });

      componentSteps.forEach((componentStepTemplate: any) => {
        const groupIds = (componentStepTemplate.inputGroups || []).map(
          (group: any) => group.id
        );

        const componentStepTarget = ensureStep(
          (step: any) =>
            step?.inputGroups?.some((group: any) =>
              groupIds.includes(group.id)
            ),
          () =>
            buildStep(
              componentStepTemplate.name,
              componentStepTemplate.description,
              []
            )
        );

        (componentStepTemplate.inputGroups || []).forEach((group: any) => {
          upsertGroupInputs(componentStepTarget, group);
        });
      });

      return {
        ...prev,
        ComponentConfig: {
          ...prev.ComponentConfig,
          steps: existingSteps,
        },
      };
    });
  }, [
    importedJSON?.Bag?.External,
    setSolution,
    solution?.Components,
    toExternalDragArg,
    toComponentConfigDragArgs,
  ]);

  const usedInputs =
    solution?.ComponentConfig?.steps?.flatMap((step: any) =>
      step.inputGroups?.flatMap((group: any) => group.inputs)
    ) || [];

  const flattenedFormData = Object.values(formData).reduce(
    (acc, val) => ({ ...acc, ...val }),
    {}
  );
  const getIsVisible = (vg: any) =>
    !vg.VisibilityField ||
    usedInputs.some(
      (arg: any) =>
        arg.Field === vg.VisibilityField &&
        ((vg.VisibilityLogical === "is" &&
          (vg.VisibilityValues ?? []).includes(
            flattenedFormData?.[arg.Field]?.[0] ?? ""
          )) ||
          (vg.VisibilityLogical === "is_not" &&
            !(vg.VisibilityValues ?? []).includes(
              flattenedFormData?.[arg.Field]?.[0] ?? ""
            )) ||
          (vg.VisibilityLogical === "gte" &&
            flattenedFormData?.[arg.Field]?.some((v: any) =>
              (vg.VisibilityValues ?? []).some(
                (value: number) => (value as number) <= parseFloat(v)
              )
            )) ||
          (vg.VisibilityLogical === "lte" &&
            flattenedFormData?.[arg.Field]?.some((v: any) =>
              (vg.VisibilityValues ?? []).some(
                (value: number) => (value as number) >= parseFloat(v)
              )
            )))
    );

  const diagramInputStep =
    diagramInputModal &&
    solution?.ComponentConfig?.steps?.[diagramInputModal.stepIndex];
  const diagramInputGroup = diagramInputStep?.inputGroups?.find(
    (group: any) => group.id === diagramInputModal?.groupId
  );
  const diagramInput =
    typeof diagramInputModal?.inputIndex === "number"
      ? diagramInputGroup?.inputs?.[diagramInputModal.inputIndex]
      : undefined;

  return (
    <div className="">
      {/* Sidebar arg ghost — portalled to body so it's never clipped */}
      {sidebarDragState &&
        ReactDOM.createPortal(
          <div
            ref={(el: HTMLDivElement | null) => {
              sidebarGhostEl.current = el;
            }}
            style={{
              position: "fixed",
              top: sidebarDragState.top,
              left: sidebarDragState.left,
              width: sidebarDragState.width,
              minWidth: 400,
              pointerEvents: "none",
              zIndex: 9999,
              opacity: 0.85,
              background: "white",
              border: "2px dashed #6c5ce7",
              borderRadius: 8,
              padding: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {sidebarDragState.arg?.__kind === "external" ? (
              <SolutionImportInput
                previewOnly
                fieldType={sidebarDragState.arg.fieldType}
                fieldKey={sidebarDragState.arg.fieldKey}
                value={sidebarDragState.arg.fieldValue}
                previewLabel={sidebarDragState.arg.Label}
              />
            ) : sidebarDragState.arg?.__kind === "component-config" ? (
              <div className="rounded px-2 py-1 w-100 text-center btn btn-light">
                <div>{sidebarDragState.arg.Label}</div>
                <div className="text-secondary text-small">
                  {sidebarDragState.arg.Description}
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex align-items-center gap-2 text-sm mb-1 fw-semibold">
                  {sidebarDragState.arg.Label}
                </div>
                {sidebarDragState.arg.Description && (
                  <div className="text-secondary text-sm mb-2">
                    {sidebarDragState.arg.Description}
                  </div>
                )}
                <InputArgumentField
                  arg={sidebarDragState.arg}
                  values={[]}
                  onChange={() => {}}
                  inputArgumentsInEditor={[]}
                />
              </>
            )}
          </div>,
          document.body
        )}
      {navDragGhost &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: navDragGhost.top,
              left: navDragGhost.left,
              width: navDragGhost.width,
              minHeight: navDragGhost.height,
              pointerEvents: "none",
              zIndex: 10001,
              opacity: 0.88,
              background: "white",
              border: "2px dashed #6c5ce7",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
              fontWeight: 700,
            }}
          >
            {navDragGhost.label}
          </div>,
          document.body
        )}
      <div className="p-3 bg-white">
        <div className="overflow-x-auto">
          <div className="d-flex align-items-center mt-3 mt-lg-0">
            <div className="border-end me-2">
              <MyIcon Icon={SolutionIcon} width={50} className="m-2" />
            </div>
            <div className="" style={{ minWidth: 300 }}>
              <div className="fs-3">Solutions</div>
              <div className="fs-3">Builder Progress</div>
              <div className="text-secondary">
                Lorem ipsum dolor sit amet consectetur.
              </div>
            </div>
            <Nav
              variant="tabs"
              className="mb-3 flex-nowrap justify-content-center justify-content-lg-start gap-2 border-0"
            >
              {solution?.ComponentConfig?.steps.map(
                (step: any, index: number) => {
                  const stepKey =
                    step.inputGroups?.[0]?.id ||
                    `${step.name}-${step.description}-${index}`;

                  if (
                    disableInputDragging &&
                    getIsVisible &&
                    !getIsVisible(step)
                  )
                    return null;

                  return (
                    <Nav.Item
                      key={stepKey}
                      draggable={!!setSolution}
                      onDragStart={(e) =>
                        handleNavStepDragStart(
                          e,
                          index,
                          step.name || `Step ${index + 1}`,
                          150,
                          96
                        )
                      }
                      onDragOver={(e) => handleNavStepDragOver(e, index)}
                      onDrop={() =>
                        handleNavStepDrop(
                          index,
                          navDropRegion?.index === index
                            ? navDropRegion.side
                            : "right"
                        )
                      }
                      onDragEnd={resetNavDragState}
                      style={{ position: "relative" }}
                    >
                      {navDropRegion?.index === index &&
                        navDragFromIndex !== null && (
                          <>
                            <div
                              style={{
                                position: "absolute",
                                top: 4,
                                bottom: 4,
                                left: -2,
                                width: 4,
                                borderRadius: 99,
                                background:
                                  navDropRegion.side === "left"
                                    ? "#6c5ce7"
                                    : "transparent",
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                top: 4,
                                bottom: 4,
                                right: -2,
                                width: 4,
                                borderRadius: 99,
                                background:
                                  navDropRegion.side === "right"
                                    ? "#6c5ce7"
                                    : "transparent",
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            />
                          </>
                        )}
                      <Nav.Link
                        onClick={() => focusStep(index)}
                        className="border-0 h-100"
                      >
                        <div
                          style={{ width: 150 }}
                          className={`border rounded p-2 px-3 position-relative d-flex flex-column h-100 ${
                            activeStepIndex === index
                              ? "border-purple shadow-purple"
                              : ""
                          } ${
                            navDropRegion?.index === index &&
                            navDragFromIndex !== null &&
                            navDragFromIndex !== index
                              ? "border-purple"
                              : ""
                          } align-items-center gap-1`}
                        >
                          <div className="badge bg-colored position-absolute top-0 start-0 translate-middle-x mt-2">
                            {index + 1}
                          </div>
                          <div className="">
                            <MyIcon Icon={SolutionIcon} width={20} />
                          </div>
                          <div className="text-navy fw-bold text-uppercase text-small text-nowrap">
                            Step {index + 1}
                          </div>
                          <div className="fw-bold">{step.name}</div>
                        </div>
                      </Nav.Link>
                    </Nav.Item>
                  );
                }
              )}
              {setSolution && solution && (
                <Nav.Item>
                  <Nav.Link
                    onClick={() => {
                      const newIndex =
                        solution?.ComponentConfig?.steps?.length ?? 0;
                      setSolution({
                        ...solution,
                        ComponentConfig: {
                          ...solution.ComponentConfig,
                          steps: [
                            ...(solution.ComponentConfig?.steps || []),
                            {
                              name: `Step ${newIndex + 1}`,
                              description:
                                "Step description goes here. Lorem ipsum dolor sit amet consectetur adipisicing elit.",
                              inputGroups: [
                                {
                                  id: `group-${Date.now()}`,
                                  name: "Input Group 1",
                                  description:
                                    "Input group description goes here. Start by dragging input elements from the left panel.",
                                  inputs: [],
                                },
                              ],
                            },
                          ],
                        },
                      });
                      focusStep(newIndex);
                    }}
                    className="border-0 h-100"
                  >
                    <div
                      style={{ width: 150 }}
                      className={`border rounded p-2 px-3 position-relative d-flex flex-column h-100 align-items-center gap-1`}
                    >
                      <div className="badge bg-colored position-absolute top-0 start-0 translate-middle-x mt-2"></div>
                      <div className="">
                        {/* <MyIcon Icon={step.icon} width={20} /> */}
                      </div>
                      <div className="text-navy fw-bold text-uppercase text-small text-nowrap"></div>
                      <div className="fw-bold">Add step</div>
                    </div>
                  </Nav.Link>
                </Nav.Item>
              )}
            </Nav>
          </div>
        </div>
      </div>
      <div className="row mt-3" style={{ height: "calc(100vh - 250px)" }}>
        {setSolution && (
          <div
            className="col-2"
            style={{ maxHeight: "100%", overflowY: "auto" }}
          >
            <button
              type="button"
              className="btn btn-colored w-100 mb-2"
              onClick={handleAddAll}
            >
              Add All
            </button>
            {solution?.Components?.map((component) => {
              const componentArgs = component.Item.InputArguments.filter(
                (arg) => arg.IsParameter && !arg.SolutionDoNotConfigure
              ).filter((arg) => {
                // Only show arguments that are not already used in the solution steps
                const isUsed = solution?.ComponentConfig?.steps?.some(
                  (step: any) =>
                    step.inputGroups.some((group: any) =>
                      group.inputs.some((input: any) => {
                        const directMatch =
                          input.ComponentUuid === component.ComponentUuid &&
                          ((arg.UUID && input.UUID === arg.UUID) ||
                            (arg.Field && input.Field === arg.Field));

                        const mergedRefMatch =
                          Array.isArray(input.MergedInputRefs) &&
                          !!arg.Field &&
                          input.MergedInputRefs.some(
                            (ref: any) =>
                              ref?.ComponentUuid === component.ComponentUuid &&
                              ref?.Field === arg.Field
                          );

                        return directMatch || mergedRefMatch;
                      })
                    )
                );
                return !isUsed;
              });
              const componentVariableGroups = [
                ...(component.Item?.VariableGroups || []),
              ].sort((a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0));
              const groupLabelByUuid = new Map(
                componentVariableGroups.map((group: any) => [
                  group.GroupUuid,
                  group.Label,
                ])
              );
              if (!groupLabelByUuid.has("")) {
                groupLabelByUuid.set("", "Default Group");
              }
              const groupedComponentArgs = componentArgs.reduce(
                (acc: Record<string, any[]>, arg: any) => {
                  const groupUuid = arg?.GroupUuid || "";
                  if (!acc[groupUuid]) acc[groupUuid] = [];
                  acc[groupUuid].push(arg);
                  return acc;
                },
                {}
              );
              const hasComponentConfigItems = [
                component.Role === "weather-monitor" ||
                  component.Role === "ping-monitor",
                component.Role === "inbound-api",
                component.Role === "inbound-message",
              ].some(Boolean);
              const isComponentDisabled =
                componentArgs.length === 0 && !hasComponentConfigItems;

              const orderedGroupKeys = [
                ...componentVariableGroups.map((group: any) => group.GroupUuid),
                ...Object.keys(groupedComponentArgs).filter(
                  (groupUuid) =>
                    groupUuid !== "" && !groupLabelByUuid.has(groupUuid)
                ),
              ];

              const renderComponentArg = (arg: any) => (
                <div
                  key={`${component.ComponentUuid}:${arg.UUID || arg.id}`}
                  className="rounded px-2 py-1 w-100 text-center btn btn-light"
                  style={{ cursor: "grab" }}
                  onPointerDown={(e) =>
                    onSidebarArgPointerDown(e, arg, component.ComponentUuid)
                  }
                >
                  <div>{arg.Label}</div>
                  <div className="text-secondary text-small">
                    {arg.Description}
                  </div>
                </div>
              );

              const isComponentConfigUsed = (arg: any) =>
                solution?.ComponentConfig?.steps?.some((step: any) =>
                  step.inputGroups.some((group: any) =>
                    group.inputs.some((input: any) => {
                      const sameUuid = input.UUID === arg.UUID;
                      const sameConfigIdentity =
                        input?.__kind === "component-config" &&
                        (input?.componentUuid === arg.componentUuid ||
                          input?.ComponentUuid === arg.componentUuid) &&
                        input?.configType === arg.configType;

                      return sameUuid || sameConfigIdentity;
                    })
                  )
                );

              // if (!componentArgs.length) return null;
              return (
                <div
                  key={component.ComponentUuid}
                  className={`border rounded p-2 mb-2 d-flex flex-column justify-content-center align-items-center gap-1 ${
                    isComponentDisabled ? "opacity-50" : ""
                  }`}
                >
                  {isComponentDisabled ? (
                    <div className="w-100">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 overflow-hidden text-navy fw-bold text-uppercase text-small text-nowrap">
                          {component.Item.Label}
                        </div>
                        <span className="p-1 px-2 ms-2 rounded-2 text-purple d-inline-block">
                          <i className="fa fa-angle-down" />
                        </span>
                      </div>
                      <div className="text-secondary text-small mt-2">
                        No variables available
                      </div>
                    </div>
                  ) : (
                    <MyCollapse
                      heading={
                        <div className="flex-grow-1 overflow-hidden text-navy fw-bold text-uppercase text-small text-nowrap">
                          {component.Item.Label}
                        </div>
                      }
                    >
                      {orderedGroupKeys.map((groupUuid: string) => {
                        const argsInGroup = [
                          ...(groupedComponentArgs[groupUuid] || []),
                        ].sort(
                          (a: any, b: any) => (a?.Order ?? 0) - (b?.Order ?? 0)
                        );

                        const groupLabel =
                          groupLabelByUuid.get(groupUuid) || "Other Variables";

                        return (
                          <div
                            key={`${component.ComponentUuid}:${groupUuid}`}
                            className="w-100 mb-2 bg-purple-gradient rounded p-1"
                          >
                            {argsInGroup.length === 0 ? (
                              <div className="w-100 opacity-50">
                                <div className="text-navy fw-bold text-uppercase text-small text-nowrap">
                                  {groupLabel}
                                </div>
                                <div className="text-secondary text-small mt-2">
                                  No variables available
                                </div>
                              </div>
                            ) : (
                              <MyCollapse
                                heading={
                                  <div className="text-navy fw-bold text-uppercase text-small text-nowrap">
                                    {groupLabel}
                                  </div>
                                }
                              >
                                <div className="d-flex flex-column gap-1">
                                  {argsInGroup.map((arg: any) =>
                                    renderComponentArg(arg)
                                  )}
                                </div>
                              </MyCollapse>
                            )}
                          </div>
                        );
                      })}

                      {(componentArgs.length > 0 ||
                        component.Role === "weather-monitor" ||
                        component.Role === "ping-monitor" ||
                        component.Role === "inbound-api" ||
                        component.Role === "inbound-message") && (
                        <div className="text-navy fw-bold text-uppercase text-small text-nowrap mt-2 mb-1">
                          Component Configuration
                        </div>
                      )}

                      {/* Component-config draggable items */}
                      {(component.Role === "weather-monitor" ||
                        component.Role === "ping-monitor") &&
                        (() => {
                          const arg = {
                            __kind: "component-config",
                            configType: component.Role,
                            componentUuid: component.ComponentUuid,
                            UUID: `component-config:${component.ComponentUuid}:monitor`,
                            Label: "Monitor Config",
                            Description: component.Item?.Label,
                          };
                          const isUsed = isComponentConfigUsed(arg);
                          if (isUsed) return null;
                          return (
                            <div
                              key={arg.UUID}
                              className="rounded px-2 py-1 w-100 text-center btn btn-light"
                              style={{ cursor: "grab" }}
                              onPointerDown={(e) =>
                                onSidebarArgPointerDown(
                                  e,
                                  arg,
                                  component.ComponentUuid
                                )
                              }
                            >
                              <div>{arg.Label}</div>
                              <div className="text-secondary text-small">
                                {arg.Description}
                              </div>
                            </div>
                          );
                        })()}

                      {component.Role === "inbound-api" &&
                        (() => {
                          const arg = {
                            __kind: "component-config",
                            configType: "inbound-api",
                            componentUuid: component.ComponentUuid,
                            UUID: `component-config:${component.ComponentUuid}:inbound-api`,
                            Label: "API Connection",
                            Description: component.Item?.Label,
                          };
                          const isUsed = isComponentConfigUsed(arg);
                          if (isUsed) return null;
                          return (
                            <div
                              key={arg.UUID}
                              className="rounded px-2 py-1 w-100 text-center btn btn-light"
                              style={{ cursor: "grab" }}
                              onPointerDown={(e) =>
                                onSidebarArgPointerDown(
                                  e,
                                  arg,
                                  component.ComponentUuid
                                )
                              }
                            >
                              <div>{arg.Label}</div>
                              <div className="text-secondary text-small">
                                {arg.Description}
                              </div>
                            </div>
                          );
                        })()}

                      {component.Role === "inbound-message" &&
                        (() => {
                          const arg = {
                            __kind: "component-config",
                            configType: "inbound-message",
                            componentUuid: component.ComponentUuid,
                            UUID: `component-config:${component.ComponentUuid}:inbound-message`,
                            Label: "Inbound Channel",
                            Description: component.Item?.Label,
                          };
                          const isUsed = isComponentConfigUsed(arg);
                          if (isUsed) return null;
                          return (
                            <div
                              key={arg.UUID}
                              className="rounded px-2 py-1 w-100 text-center btn btn-light"
                              style={{ cursor: "grab" }}
                              onPointerDown={(e) =>
                                onSidebarArgPointerDown(
                                  e,
                                  arg,
                                  component.ComponentUuid
                                )
                              }
                            >
                              <div>{arg.Label}</div>
                              <div className="text-secondary text-small">
                                {arg.Description}
                              </div>
                            </div>
                          );
                        })()}
                    </MyCollapse>
                  )}
                </div>
              );
            })}

            {Object.entries(importedJSON?.Bag?.External || {}).map(
              ([fieldType, fields]: [string, any]) => {
                const fieldEntries = Object.entries(fields || {});
                if (!fieldEntries.length) return null;

                return (
                  <div
                    key={fieldType}
                    className="border rounded p-2 mb-2 d-flex flex-column align-items-center gap-1"
                  >
                    <MyCollapse
                      heading={
                        <div className="flex-grow-1 overflow-hidden text-navy fw-bold text-uppercase text-small text-nowrap">
                          External {fieldType}
                        </div>
                      }
                    >
                      {fieldEntries
                        .map(([fieldKey, fieldValue]: [string, any]) =>
                          toExternalDragArg(fieldType, fieldKey, fieldValue)
                        )
                        .filter((arg) => {
                          const isUsed = solution?.ComponentConfig?.steps?.some(
                            (step: any) =>
                              step.inputGroups.some((group: any) =>
                                group.inputs.some((input: any) => {
                                  const directMatch = input.UUID === arg.UUID;
                                  const mergedRefMatch =
                                    Array.isArray(input.MergedInputRefs) &&
                                    input.MergedInputRefs.some(
                                      (ref: any) =>
                                        (ref?.__kind === "external" &&
                                          ref?.fieldType === arg.fieldType &&
                                          ref?.fieldKey === arg.fieldKey) ||
                                        (ref?.ComponentUuid ===
                                          `external:${arg.fieldType}` &&
                                          ref?.Field === arg.fieldKey)
                                    );

                                  return directMatch || mergedRefMatch;
                                })
                              )
                          );
                          return !isUsed;
                        })
                        .map((arg) => (
                          <div
                            key={arg.UUID}
                            className="rounded px-2 py-1 w-100 text-center btn btn-light"
                            style={{ cursor: "grab" }}
                            onPointerDown={(e) =>
                              onSidebarArgPointerDown(
                                e,
                                arg
                                // component.ComponentUuid
                              )
                            }
                          >
                            <div>{arg.Label}</div>
                            <div className="text-secondary text-small">
                              {arg.Description}
                            </div>
                          </div>
                        ))}
                    </MyCollapse>
                  </div>
                );
              }
            )}
          </div>
        )}
        <div
          className="col border-end overflow-auto"
          ref={setupFormContainerRef}
          style={{ maxHeight: "100%" }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div>
              <Form.Check
                type="switch"
                className="flex-row-reverse ms-n5"
                label={
                  <div className="me-5">
                    <AiIcon width={30} className="" />
                    <span className="text-navy fw-bold me-1">AI</span>
                    <span>Assistant</span>
                  </div>
                }
                id="ai-switch"
              />
            </div>
            <div>
              <ProfileIcon width={30} className="m-2" />
            </div>
          </div>
          <Tab.Container id="solution-steps-tabs" activeKey={activeStepIndex}>
            <Tab.Content>
              {solution?.ComponentConfig?.steps.map(
                (step: any, index: number) => {
                  const stepKey =
                    step.inputGroups?.[0]?.id ||
                    `${step.name}-${step.description}-${index}`;
                  return (
                    <Tab.Pane key={stepKey} eventKey={index}>
                      <SolutionStep
                        step={step}
                        stepIndex={index}
                        totalSteps={
                          solution?.ComponentConfig?.steps?.length || 0
                        }
                        focusedInputNodeKey={focusedInputNodeKey}
                        onPreviousStep={() =>
                          focusStep(Math.max(0, activeStepIndex - 1))
                        }
                        onNextStep={() =>
                          focusStep(
                            Math.min(
                              (solution?.ComponentConfig?.steps?.length || 1) -
                                1,
                              activeStepIndex + 1
                            )
                          )
                        }
                        sidebarDragging={!!sidebarDragState}
                        sidebarOverInput={sidebarOverInput}
                        setStep={
                          setSolution
                            ? (updatedStep: any) => {
                                setSolution({
                                  ...solution,
                                  ComponentConfig: {
                                    ...solution.ComponentConfig,
                                    steps: solution.ComponentConfig.steps.map(
                                      (s: any, i: number) =>
                                        i === index ? updatedStep : s
                                    ),
                                  },
                                });
                              }
                            : () => {}
                        }
                        onDelete={
                          setSolution
                            ? () => {
                                setSolution({
                                  ...solution,
                                  ComponentConfig: {
                                    ...solution.ComponentConfig,
                                    steps:
                                      solution.ComponentConfig.steps.filter(
                                        (_: any, i: number) => i !== index
                                      ),
                                  },
                                });
                                const nextStepIndex = Math.max(
                                  0,
                                  activeStepIndex > index
                                    ? activeStepIndex - 1
                                    : Math.min(
                                        activeStepIndex,
                                        solution.ComponentConfig.steps.length -
                                          2
                                      )
                                );
                                focusStep(nextStepIndex);
                              }
                            : undefined
                        }
                        formData={formData}
                        setFormData={setFormData}
                        importContext={{
                          importedJSON,
                          setImportedJSON,
                          manualFields,
                          setManualFields,
                          contactSets: contactSetsData?.contactSets || [],
                          reportingTopics: reportingTopicsData?.data || [],
                          apiProfiles,
                          setApiProfiles,
                          updateApiConnection: updateApiConnection as any,
                          monitors,
                          setMonitors,
                          inboundAPIs,
                          setInboundAPIs,
                          inboundCampaigns,
                          setInboundCampaigns,
                          disableInputEditing,
                          disableInputDragging,
                          disableStructureEditing: isUserMode,
                        }}
                        onInputFocus={(inputGroupId: string, input?: any) => {
                          setFocusedStepIndex(index);
                          setFocusedInputGroupId(
                            `step:${index}|group:${inputGroupId}`
                          );
                          setFocusedInputNodeKey(
                            getInputFocusKey(index, inputGroupId, input)
                          );
                        }}
                        onInputBlur={(inputGroupId?: string, input?: any) => {
                          const blurKey = inputGroupId
                            ? getInputFocusKey(index, inputGroupId, input)
                            : null;
                          setFocusedInputNodeKey((prev) =>
                            prev && blurKey && prev !== blurKey ? prev : null
                          );
                          setFocusedInputGroupId((prev) =>
                            prev &&
                            inputGroupId &&
                            prev !== `step:${index}|group:${inputGroupId}`
                              ? prev
                              : null
                          );
                        }}
                        usedInputs={usedInputs}
                        getIsVisible={getIsVisible}
                      />
                    </Tab.Pane>
                  );
                }
              )}
            </Tab.Content>
          </Tab.Container>
        </div>
        <div className="col-5 h-100">
          <MyModal
            heading={
              diagramInput?.Label ? `Edit ${diagramInput.Label}` : "Edit input"
            }
            label={null}
            buttonClassName="d-none"
            show={
              !!diagramInputModal &&
              !!diagramInputStep &&
              !!diagramInputGroup &&
              !!diagramInput
            }
            onHide={() => setDiagramInputModal(null)}
            size="lg"
          >
            {diagramInputModal && diagramInputStep && diagramInputGroup ? (
              <SolutionStepInputGroup
                inputGroup={diagramInputGroup}
                step={diagramInputStep}
                setStep={
                  setSolution && solution
                    ? (updatedStep: any) => {
                        setSolution({
                          ...solution,
                          ComponentConfig: {
                            ...solution.ComponentConfig,
                            steps: solution.ComponentConfig.steps.map(
                              (step: any, stepIndex: number) =>
                                stepIndex === diagramInputModal.stepIndex
                                  ? updatedStep
                                  : step
                            ),
                          },
                        });
                      }
                    : () => {}
                }
                disableInputEditing={disableInputEditing}
                disableInputDragging
                formData={formData}
                setFormData={setFormData}
                onGroupDragHandlePointerDown={() => {}}
                onStartInputDrag={() => {}}
                importContext={{
                  importedJSON,
                  setImportedJSON,
                  manualFields,
                  setManualFields,
                  contactSets: contactSetsData?.contactSets || [],
                  reportingTopics: reportingTopicsData?.data || [],
                  apiProfiles,
                  setApiProfiles,
                  updateApiConnection: updateApiConnection as any,
                  monitors,
                  setMonitors,
                  inboundAPIs,
                  setInboundAPIs,
                  inboundCampaigns,
                  setInboundCampaigns,
                  disableInputEditing,
                  disableInputDragging: true,
                  disableStructureEditing: isUserMode,
                }}
                usedInputs={usedInputs}
                getIsVisible={getIsVisible}
                inputIndexFilter={diagramInputModal.inputIndex}
                hideInputGroupHeader
              />
            ) : null}
          </MyModal>
          <SolutionFlowOverview
            solution={solution}
            activeStepIndex={focusedStepIndex ?? activeStepIndex}
            focusedInputGroupId={focusedInputGroupId}
            focusedInputNodeKey={focusedInputNodeKey}
            onNodeClick={handleFlowNodeClick}
          />
        </div>
      </div>
    </div>
  );
};

export default SolutionSetup;
